import { formatDateString } from "@circle-react/helpers/dateTimeHelpers/getLocalizedDate";

import { useCurrentPlanApi } from "@/react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import { distanceBetweenDates } from "@/react/helpers/dateTimeHelpers";
import { isDowngrading } from "@/react/helpers/planHelpers";
import type { PlanTrial } from "@/react/types/CurrentCommunity";
import type { CurrentPlan } from "@/react/types/CurrentPlan";

import type { UpgradePlan } from "../types";
import { useUpgradePlanContext } from "../UpgradePlanContext";

const getIsDowngradingPlan = (
  currentPlan: CurrentPlan | null | undefined,
  nextPlan: UpgradePlan | null,
) => {
  if (!currentPlan?.tier || !nextPlan?.tier) return false;
  return isDowngrading(currentPlan.tier, nextPlan.tier);
};

const getPlanTrialEndDateFormatted = (planTrial: PlanTrial | null) => {
  if (!planTrial?.expires_at) return "";
  return formatDateString(planTrial.expires_at, {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
};

const getPlanTrialDaysLeft = (planTrial: PlanTrial | null) => {
  if (!planTrial?.expires_at) return 0;
  return distanceBetweenDates(new Date(), new Date(planTrial.expires_at));
};

export const useBlockPlanTrialDowngrade = () => {
  const { plan: nextPlan, isUpdatingCommunityHubPlan } =
    useUpgradePlanContext();
  const { currentPlan, isCurrentPlanLoading } = useCurrentPlanApi();

  const planTrial = currentPlan?.plan_trial || null;
  const isPlanTrialActive = planTrial?.active || false;

  const isDowngradingPlan = getIsDowngradingPlan(currentPlan, nextPlan);

  const shouldBlockPlanTrialDowngrade =
    isUpdatingCommunityHubPlan && isPlanTrialActive && isDowngradingPlan;

  const planTrialEndDateFormatted = getPlanTrialEndDateFormatted(planTrial);

  const planTrialDaysLeft = getPlanTrialDaysLeft(planTrial);

  const handleContactSupportOnClick = () => {
    window.open("mailto:support@circle.so", "emailWindow");
  };

  return {
    isLoading: isCurrentPlanLoading,
    shouldBlockPlanTrialDowngrade,
    planTrial,
    planTrialEndDateFormatted,
    planTrialDaysLeft,
    handleContactSupportOnClick,
  };
};
